import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  PolicyDocumentLayoutJSON,
  PolicyModalLayoutJSON,
} from "../Json/OrderDetailsLayoutJSON";
import {
  PolicyDocumentsFieldsJSON,
  PolicyModalFieldsJSON,
} from "../Json/OrderDetailsFieldsJSON";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Button, Form } from "react-bootstrap";
import { renderElement } from "../../../../Components/Common/FormRendererV2/Renderer";
import {
  editPolicyModal,
  openPolicyModal,
  resetFormState,
  setRendererFormState,
  setSelectValidError,
  updateRendererFormState,
} from "../../../../slices/common/reducer";
import {
  readPolicy,
  updatePolicy,
  getAdditionalMembers,
} from "../OrderService";
import { useParams } from "react-router-dom";
import DocumentUploader from "../../../../Components/Common/Documents/Documents";
import dateFormat from "dateformat";

const toDateValue = (value) =>
  value ? String(value).split("T")[0].split(" ")[0] : "";

// Cover runs up to, but not including, the anniversary of the start date:
// 17-09-2026 for a 3 year tenure expires on 16-09-2029.
const calculateEndDate = (startDate, tenure) => {
  const [year, month, day] = toDateValue(startDate).split("-").map(Number);
  const years = parseInt(tenure, 10);

  if (!year || !month || !day || !years) return null;

  const endDate = new Date(year, month - 1, day);
  endDate.setFullYear(endDate.getFullYear() + years);
  endDate.setDate(endDate.getDate() - 1);

  return dateFormat(endDate, "yyyy-mm-dd");
};

const TENURE_OPTIONS = ["1", "2", "3"];
const DEFAULT_TENURE = "1";
const deriveTenure = (startDate, endDate) => {
  const end = toDateValue(endDate);

  if (!end) return DEFAULT_TENURE;

  return (
    TENURE_OPTIONS.find((tenure) => calculateEndDate(startDate, tenure) === end) ||
    ""
  );
};

function PolicyModal() {
  const dispatch = useDispatch();
  const [members, setMembers] = useState([]);

  const [openAccordion, setOpenAccordion] = useState(null);
  const [memberFormData, setMemberFormData] = useState({});
  const policyModal = useSelector((state) => state.common.policyModalShow);
  const formState = useSelector((state) => state.common.formRendererState);
  const [memberDataVersion, setMemberDataVersion] = useState(0);
  const { id } = useParams();
  const policyData = useSelector(
    (state) => state.order.selectedOrders.policyData
  );

  const selectedPolicy = useSelector(
    (state) => state.order.selectedOrders.selectedPolicyData
  );


  const files = useSelector(
    (state) => state.order.selectedOrders.policyData.files || {}
  );



  const filteredLayoutJSON = PolicyModalLayoutJSON;

  // Policy end date follows from the start date and the selected tenure.
  // The end date is left alone when neither of those changes, so a value
  // already on the policy - or one typed in by hand - is not overwritten.
  useEffect(() => {
    const endDate = calculateEndDate(
      formState?.policy_start_date,
      formState?.policy_tenure
    );

    if (endDate && endDate !== toDateValue(formState?.policy_end_date)) {
      dispatch(setRendererFormState({ key: "policy_end_date", value: endDate }));
    }
  }, [formState?.policy_start_date, formState?.policy_tenure]);

  // Preselect the tenure whenever the modal opens without one.
  useEffect(() => {
    if (!policyModal || formState?.policy_tenure) return;
    const tenure = deriveTenure(
      formState?.policy_start_date,
      formState?.policy_end_date
    );

    if (tenure) {
      dispatch(setRendererFormState({ key: "policy_tenure", value: tenure }));
    }
  }, [policyModal]);

  useEffect(() => {
    setMembers([]);
    if (policyModal && selectedPolicy) {
      const otherPolicies = policyData?.length > 0
        ? policyData.filter(
          (policy) =>
            policy.sub_product_id === selectedPolicy.sub_product_id &&
            policy.is_member === true
        )
        : [];

      if (otherPolicies.length > 0) {
        const mappedMembers = otherPolicies.map((policy) => {
          let mediaId = null;
          let fileObj = null;
          if (policy.files) {
            if (policy.files.policycopy) {
              fileObj = policy.files.policycopy;
            } else if (policy.files.policycopy_media_id) {
              fileObj = policy.files.policycopy_media_id;
            } else if (policy.files[policy.entityid]) {
              fileObj = policy.files[policy.entityid];
            }
          }
          if (fileObj) {
            mediaId = fileObj.media_id;
          }
          if (!mediaId && policy.policycopy_media_id) {
            mediaId = policy.policycopy_media_id;
          }

          return {
            memberName: policy.policy_holder_name || "",
            policyNumber: policy.policy_no || "",
            policyStartDate: policy.policy_start_date || "",
            policyEndDate: policy.policy_end_date || "",
            policyIssueDate: policy.issue_date || "",
            retroActiveDate: policy.retro_active_date || "",
            status: policy.status || "",
            insurer: policy.oem_name || "",
            policyHolderName: policy.policy_holder_name || "",
            premiumInsurer: (policy.premium !== undefined && policy.premium !== null) ? policy.premium : "",
            is_member: policy.is_member,
            policyReceivedDate: policy.policy_received_date || "",
            uploadDate: (fileObj && fileObj.created_date) || policy.upload_date || "",
            modifiedDate: (fileObj && fileObj.modified_date) || "",
            entityid: policy.entityid,
            policycopy_media_id: mediaId || "",
            files: policy.files || null,
          };
        });
        setMembers(mappedMembers);

        // Map and dispatch the file information of other policies to formState
        otherPolicies.forEach((p) => {
          let fileObj = null;
          if (p.files) {
            if (p.files.policycopy) {
              fileObj = p.files.policycopy;
            } else if (p.files.policycopy_media_id) {
              fileObj = p.files.policycopy_media_id;
            } else if (p.files[p.entityid]) {
              fileObj = p.files[p.entityid];
            }
          }
          if (!fileObj && p.policycopy_media_id) {
            fileObj = {
              media_id: p.policycopy_media_id,
              file_name: p.policy_copy_name || p.fileName || "policy.pdf",
              media_url: p.policy_copy_url || p.mediaUrl || "",
            };
          }

          if (fileObj && p.entityid) {
            dispatch(setRendererFormState({
              key: `${p.entityid}`,
              value: {
                media_id: fileObj.media_id,
                file_name: fileObj.file_name || "policy.pdf",
                media_url: fileObj.media_url || "",
              }
            }));
          }
        });

        setMemberDataVersion(v => v + 1);
      } else if (selectedPolicy.parentid) {
        getAdditionalMembers(selectedPolicy.entityid).then((data) => {
          setMembers(data || []);
          setMemberDataVersion(v => v + 1);
        });
      }
    }
  }, [policyModal, selectedPolicy?.entityid, policyData]);


  useEffect(() => {
    if (members.length > 0) {
      const initialData = {};
      members.forEach((member, index) => {
        let fileObj = null;
        if (member.files) {
          if (member.files.policycopy) {
            fileObj = member.files.policycopy;
          } else if (member.files.policycopy_media_id) {
            fileObj = member.files.policycopy_media_id;
          } else if (member.files[member.entityid]) {
            fileObj = member.files[member.entityid];
          }
        }
        initialData[index] = {
          entityid: member.entityid || "",
          policyNumber: member.policyNumber || member.policy_no || "",
          policyStartDate: member.policyStartDate || member.policy_start_date || "",
          policyEndDate: member.policyEndDate || member.policy_end_date || "",
          policyIssueDate: member.policyIssueDate || member.issue_date || "",
          retroActiveDate: member.retroActiveDate || member.retro_active_date || "",
          status: member.status || "",
          insurer: member.insurer || member.oem_name || "",
          policyHolderName: member.policyHolderName || member.policy_holder_name || "",
          premiumInsurer: (member.premiumInsurer !== undefined && member.premiumInsurer !== null && member.premiumInsurer !== "") ? member.premiumInsurer : ((member.is_member !== undefined) ? member.is_member : ((member.isMember !== undefined && member.isMember !== null) ? member.isMember : true)),
          policyReceivedDate: member.policyReceivedDate || member.policy_received_date || "",
          uploadDate: (fileObj && fileObj.created_date) || member.uploadDate || member.upload_date || "",
          modifiedDate: (fileObj && fileObj.modified_date) || member.modifiedDate || member.modified_date || "",
          policycopy_media_id: member.policycopy_media_id || "",
        };
      });
      setMemberFormData(initialData);
    }
  }, [members]);

  const handleClose = () => {
    setMembers([]);
    dispatch(openPolicyModal(false));
    dispatch(editPolicyModal(false));
    dispatch(resetFormState());
    dispatch(setSelectValidError(false));
  };

  // useEffect(() => {
  //   if (id) {
  //     readPolicy(id, "ODR");
  //   }
  // }, [id]);

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    const updatedMemberFormData = { ...memberFormData };
    members.forEach((member, index) => {
      const n = index + 1;
      const statusFromFormState = formState[`member_${n}_status`];
      if (statusFromFormState) {
        updatedMemberFormData[index] = {
          ...updatedMemberFormData[index],
          status: statusFromFormState,
        };
      }
    });
    // policy_tenure only drives the end date in the UI, it is not a stored field
    const { policy_tenure, ...policyPayload } = formState;
    await updatePolicy(
      policyPayload,
      "PCY",
      formState.entityid,
      updatedMemberFormData
    );
    if (id) {
      await readPolicy(id, "ODR");
    }
    dispatch(openPolicyModal(false));
  };

  const generateMemberFields = () => {
    return members.map((member, index) => {
      const n = index + 1;
      const isOpen = openAccordion === index;
      const data = memberFormData[index] || {};



      return (
        <div key={`member_${n}_${memberDataVersion}`} className="mb-2 border rounded">

          {/* Accordion Header */}
          <div
            className="p-3 d-flex justify-content-between align-items-center"
            style={{ cursor: "pointer", backgroundColor: "#f8f9fa" }}
            onClick={() => setOpenAccordion(isOpen ? null : index)}
          >
            <div>
              <strong>Member {n} — {member.memberName || ""}</strong>
              <div className="text-muted mt-1" style={{ fontSize: "0.85rem" }}>
                <span className="me-3"><strong>Policy No:</strong> {data.policyNumber || "—"}</span>
                <span className="me-3"><strong>Start Date:</strong> {data.policyStartDate || "—"}</span>
                <span><strong>End Date:</strong> {data.policyEndDate || "—"}</span>
              </div>
            </div>
            <span>{isOpen ? "▲" : "▼"}</span>
          </div>

          {/* Accordion Body */}
          {/* Accordion Body */}
          {isOpen && (
            <div className="p-3">
              <div className="row mb-2">
                <div className="col-md-6">
                  <label>Policy Holder Name</label>
                  <input className="form-control" value={data.policyHolderName || ""} disabled />
                </div>
                <div className="col-md-6">
                  <label>Policy Number <span className="text-danger">*</span></label>
                  <input className="form-control" value={data.policyNumber || ""} required
                    onChange={(e) => setMemberFormData(prev => ({
                      ...prev, [index]: { ...prev[index], policyNumber: e.target.value }
                    }))} />
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-6">
                  <label>Insurer</label>
                  <input className="form-control" value={data.insurer || ""} disabled />
                </div>
                <div className="col-md-6">
                  {renderElement(
                    { elementid: `member_${n}_status`, elementtype: "customSelect", child: [] },
                    {
                      [`member_${n}_status-customSelect`]: {
                        elementid: `member_${n}_status`,
                        elementtype: "customSelect",
                        "database-label": `member_${n}_status`,
                        "data-datasource": "customvalues",
                        "data-customvalue": "AWAITED_UNDERWRITING#DETAILS_AWAITED#REJECTED#ISSUED",
                        "display-fieldlabel": "Status",
                        "display-labelposition": "top",
                        "display-placeholder": "Select",
                        "display-required": 1,
                      }
                    },
                    { ...formState, [`member_${n}_status`]: formState[`member_${n}_status`] ?? data.status }
                  )}
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-6">
                  <label>Policy Start Date <span className="text-danger">*</span></label>
                  <input type="date" className="form-control" value={data.policyStartDate ? data.policyStartDate.split('T')[0].split(' ')[0] : ""} required
                    onChange={(e) => setMemberFormData(prev => ({
                      ...prev, [index]: { ...prev[index], policyStartDate: e.target.value }
                    }))} />
                </div>
                <div className="col-md-6">
                  <label>Policy End Date <span className="text-danger">*</span></label>
                  <input type="date" className="form-control" value={data.policyEndDate ? data.policyEndDate.split('T')[0].split(' ')[0] : ""} required
                    onChange={(e) => setMemberFormData(prev => ({
                      ...prev, [index]: { ...prev[index], policyEndDate: e.target.value }
                    }))} />
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-6">
                  <label>Premium Insurer</label>
                  <input className="form-control" value={(data.premiumInsurer !== undefined && data.premiumInsurer !== null && data.premiumInsurer !== "") ? data.premiumInsurer : "0.00"} disabled />
                </div>
                <div className="col-md-6">
                  <label>Retroactive Date</label>
                  <input type="date" className="form-control" value={data.retroActiveDate ? data.retroActiveDate.split('T')[0].split(' ')[0] : ""}
                    onChange={(e) => setMemberFormData(prev => ({
                      ...prev, [index]: { ...prev[index], retroActiveDate: e.target.value }
                    }))} />
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-6">
                  <label>Issue Date <span className="text-danger">*</span></label>
                  <input type="date" className="form-control" value={data.policyIssueDate ? data.policyIssueDate.split('T')[0].split(' ')[0] : ""} required
                    onChange={(e) => setMemberFormData(prev => ({
                      ...prev, [index]: { ...prev[index], policyIssueDate: e.target.value }
                    }))} />
                </div>
                <div className="col-md-6">
                  <label>Policy Received Date</label>
                  <input type="date" className="form-control" value={data.policyReceivedDate ? data.policyReceivedDate.split('T')[0].split(' ')[0] : ""}
                    onChange={(e) => setMemberFormData(prev => ({
                      ...prev, [index]: { ...prev[index], policyReceivedDate: e.target.value }
                    }))} />
                </div>
              </div>
              <div className="row mb-2">
                <div className="col-md-6">
                  <label>Upload Date</label>
                  <input type="date" className="form-control"
                    value={data.uploadDate ? data.uploadDate.split('T')[0].split(' ')[0] : ""} disabled />
                </div>
                <div className="col-md-6">
                  <label>Modified Date</label>
                  <input type="date" className="form-control"
                    value={data.modifiedDate ? data.modifiedDate.split('T')[0].split(' ')[0] : ""} disabled />
                </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  {renderElement(
                    { elementid: `${member.entityid}_policy_copy`, elementtype: "documentFile", child: [] },
                    {
                      [`${member.entityid}_policy_copy-documentFile`]: {
                        elementid: `${member.entityid}_policy_copy`,
                        elementtype: "documentFile",
                        "display-fieldlabel": `Member ${n} Policy Copy`,
                        "database-label": `${member.entityid}`,
                        "display-labelposition": "top",
                        "display-required": 1,
                      }
                    },
                    { ...formState }
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <Modal
      isOpen={policyModal}
      onHide={() => handleClose()}
      centered
      backdrop="static"
      keyboard={false}
      className="border-0"
      modalClassName="modal fade zoomIn"
      toggle={handleClose}
      size="lg"
    >
      <ModalHeader className="p-3 bg-info-subtle" toggle={handleClose}>
        Policy
      </ModalHeader>
      <Form onSubmit={handleSave}>
        <ModalBody
          className="policy-modal-wrapper"
          style={{ height: "calc(100vh - 400px)", overflowY: "scroll" }}
        >
          {filteredLayoutJSON.map((element) => (
            <div key={`${element.elementid}-${memberDataVersion}`}>
              {element.child.map((child) =>
                renderElement(child, PolicyModalFieldsJSON, { ...formState })
              )}
            </div>
          ))}
          {members && members.length > 0 && generateMemberFields()}
        </ModalBody>
        <ModalFooter>
          <Button
            type="submit"
            className="primary primary_button"
            id="addNewMember"
          >
            Save
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

export default PolicyModal;
